import { useCallback, useEffect, useState } from 'react';
import { authApi, type InvitePreview } from '../lib/authApi';
import { useAuth } from './AuthContext';
import { AuthError, AuthScreen } from './AuthScreen';

const ROLE_LABEL: Record<string, string> = { admin: 'администратор', member: 'участник' };

/**
 * Strips `?invite=` so a reload does not replay this screen, and points the
 * address at the invited board when the invite was for one — landing on the
 * dashboard and hunting for it would be a strange end to following a link to
 * a specific board.
 */
function clearInviteParam(boardId?: string | null): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('invite');
  if (boardId) url.searchParams.set('b', boardId);
  else url.searchParams.delete('b');
  window.history.replaceState(null, '', url.toString());
}

/**
 * Landing page for an org invite link (`/?invite=<token>`).
 *
 * Three outcomes: the token is dead (show why and offer to log in normally),
 * the visitor is already signed in (join immediately, no form needed), or
 * they are not — pick "I have an account" vs "create one", both of which end
 * in the same joined-organisation state.
 */
export function InviteLandingPage({ token }: { token: string }) {
  const { user, refresh } = useAuth();
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [mode, setMode] = useState<'register' | 'login'>('register');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);
  const [joinFailed, setJoinFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await authApi.previewInvite(token);
        if (!cancelled) setPreview(result);
      } catch {
        if (!cancelled) setLoadError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const finish = useCallback(
    async (boardId?: string | null) => {
      clearInviteParam(boardId);
      await refresh();
      // The board route reads its id from the URL on mount, and `refresh`
      // only re-renders the gate, so a board invite needs the reload to
      // actually arrive there.
      if (boardId) window.location.reload();
    },
    [refresh],
  );

  // Already signed in: the invite link only has to add membership, so there
  // is nothing for the visitor to fill in.
  useEffect(() => {
    if (!user || !preview?.valid || joining || joinFailed) return;
    setJoining(true);
    (async () => {
      try {
        const result = await authApi.acceptInvite(token);
        await finish(result.boardId);
      } catch {
        // Already a member of the org with no board grant left to redeem, or
        // the token died between preview and accept — stay here with an
        // explanation instead of bouncing to a board they cannot open.
        setJoining(false);
        setJoinFailed(true);
        setError(
          preview.boardId
            ? 'Не удалось открыть доступ к доске. Попросите новую ссылку у владельца.'
            : 'Не удалось принять приглашение. Попросите новую ссылку.',
        );
      }
    })();
  }, [user, preview, token, joining, joinFailed, finish]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === 'register') {
        const registered = await authApi.register({ email, password, name, inviteToken: token });
        await finish(registered.boardId ?? preview?.boardId ?? null);
      } else {
        await authApi.login(email, password);
        const result = await authApi.acceptInvite(token);
        await finish(result.boardId);
      }
    } catch {
      setError(
        mode === 'register'
          ? 'Не удалось создать аккаунт — возможно, эта почта уже используется.'
          : 'Неверная почта или пароль, либо ссылка больше не действует.',
      );
    } finally {
      setBusy(false);
    }
  };

  if (loadError) {
    return (
      <AuthScreen title="Приглашение">
        <AuthError message="Сервер недоступен. Проверьте, что запущены оба процесса — web и server." />
      </AuthScreen>
    );
  }

  if (!preview) {
    return (
      <AuthScreen title="Приглашение">
        <p className="auth-hint">Проверяем ссылку…</p>
      </AuthScreen>
    );
  }

  if (!preview.valid) {
    return (
      <AuthScreen
        title="Ссылка недействительна"
        subtitle="Эту ссылку-приглашение отозвали, или она никогда не существовала."
      >
        <button
          type="button"
          className="primary-btn auth-submit"
          onClick={() => {
            clearInviteParam();
            void refresh();
          }}
        >
          Перейти ко входу
        </button>
      </AuthScreen>
    );
  }

  if (joinFailed) {
    return (
      <AuthScreen title="Не удалось присоединиться" subtitle={error ?? undefined}>
        <button
          type="button"
          className="primary-btn auth-submit"
          onClick={() => {
            clearInviteParam();
            void refresh();
          }}
        >
          К списку досок
        </button>
      </AuthScreen>
    );
  }

  if (user || joining) {
    return (
      <AuthScreen
        title="Присоединяемся…"
        subtitle={
          preview.asStudentOf
            ? `Открываем доску «${preview.boardName}» — вы ученик ${preview.asStudentOf}.`
            : preview.boardName
              ? `Открываем доску «${preview.boardName}».`
              : `Добавляем вас в «${preview.orgName}».`
        }
      >
        <p className="auth-hint">Секунду…</p>
      </AuthScreen>
    );
  }

  return (
    <AuthScreen
      title="Вас пригласили"
      subtitle={
        preview.asStudentOf
          ? `Вас пригласили на доску «${preview.boardName}» — учеником ${preview.asStudentOf} (${preview.orgName}).`
          : preview.boardName
            ? `Вас пригласили на доску «${preview.boardName}» (${preview.orgName}).`
            : `Вас пригласили в «${preview.orgName}» как ${ROLE_LABEL[preview.role ?? 'member']}.`
      }
    >
      <div className="auth-tabs">
        <button
          type="button"
          className={mode === 'register' ? 'is-active' : ''}
          onClick={() => setMode('register')}
        >
          Создать аккаунт
        </button>
        <button
          type="button"
          className={mode === 'login' ? 'is-active' : ''}
          onClick={() => setMode('login')}
        >
          У меня есть аккаунт
        </button>
      </div>

      <form className="auth-form" onSubmit={submit}>
        {mode === 'register' && (
          <label className="auth-field">
            <span>Имя</span>
            <input
              type="text"
              autoFocus
              required
              maxLength={60}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
        )}
        <label className="auth-field">
          <span>Почта</span>
          <input
            type="email"
            required
            autoFocus={mode === 'login'}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
        </label>
        <label className="auth-field">
          <span>Пароль</span>
          <input
            type="password"
            required
            minLength={mode === 'register' ? 8 : undefined}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={mode === 'register' ? 'Не короче 8 символов' : undefined}
          />
        </label>

        <AuthError message={error} />

        <button type="submit" className="primary-btn auth-submit" disabled={busy}>
          {busy
            ? 'Секунду…'
            : mode === 'register'
              ? 'Создать и присоединиться'
              : 'Войти и присоединиться'}
        </button>
      </form>
    </AuthScreen>
  );
}
